import { useState } from 'react'
import styles from './ToolHeader.module.css'
import ConfirmModal from './ConfirmModal'

interface ToolHeaderProps {
  categoryLabel: string
  toolName: string
  onClear: () => void
  onSave: () => Promise<void> | void
  onMarkComplete: () => Promise<void> | void
  isCompleted?: boolean
}

export default function ToolHeader({
  categoryLabel,
  toolName,
  onClear,
  onSave,
  onMarkComplete,
  isCompleted = false,
}: ToolHeaderProps) {
  const [status, setStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [showClearConfirm, setShowClearConfirm] = useState(false)

  const showStatus = (message: string, type: 'success' | 'error' = 'success') => {
    setStatus({ message, type })
    setTimeout(() => setStatus(null), 3000)
  }

  const handleSave = async () => {
    try {
      await onSave()
      showStatus('Saved successfully')
    } catch {
      showStatus('Failed to save', 'error')
    }
  }

  const handleClearConfirmed = () => {
    setShowClearConfirm(false)
    onClear()
    showStatus('Data cleared')
  }

  const handleMarkComplete = async () => {
    try {
      await onMarkComplete()
      showStatus('Marked as complete')
    } catch {
      showStatus('Failed to mark complete', 'error')
    }
  }

  return (
    <>
      <div className={styles.head} data-testid="tool-header">
        <div className={styles.left}>
          <div className={styles.badge} data-testid="tool-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <div>
            <div className={styles.tag} data-testid="tool-category-label">
              Teamship Tools &rsaquo; {categoryLabel}
            </div>
            <h1 className={styles.title} data-testid="tool-title">{toolName}</h1>
          </div>
        </div>
        <div className={styles.actionsWrap}>
          <div className={styles.actions} data-testid="tool-actions">
            <button className={styles.btn} onClick={() => setShowClearConfirm(true)} data-testid="tool-clear-btn">
              ↺ Clear
            </button>
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSave} data-testid="tool-save-btn">
              Save
            </button>
            <button
              className={`${styles.btn} ${isCompleted ? styles.btnCompleted : styles.btnComplete}`}
              onClick={handleMarkComplete}
              disabled={isCompleted}
              data-testid="tool-complete-btn"
            >
              {isCompleted ? '✓ Completed' : '✓ Mark as Complete'}
            </button>
          </div>
          {status && (
            <div
              className={status.type === 'success' ? styles.statusSuccess : styles.statusError}
              data-testid="tool-action-status"
            >
              {status.message}
            </div>
          )}
        </div>
      </div>

      {showClearConfirm && (
        <ConfirmModal
          title="Clear all data?"
          message="This will permanently remove all your responses for this tool. This cannot be undone."
          confirmLabel="Clear data"
          variant="danger"
          onConfirm={handleClearConfirmed}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}
    </>
  )
}
